import { Component, ElementRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { validateRequired } from '@app/schema-form/utils/utils';
import { I18nService } from '@app/shared/utils/i18n.service';
import { getDebug, isEmpty } from '@app/shared/utils/util';
import type { Editor } from '@aurigma/design-editor-iframe';
import type { ISchema } from 'ngx-schema-form';

export type SchemaModel = Record<string, string | number | Editor.IUserInfo | undefined>;

/** Base class for schema form components. */
@Component({
	selector: 'g-base-schema-form',
	templateUrl: './base-schema-form.component.html',
	styleUrls: ['./base-schema-form.component.scss'],
	standalone: false,
})
export class BaseSchemaFormComponent {
	protected readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
	protected readonly i18n = inject(I18nService);

	/** Debug logger */
	protected readonly debug = getDebug('BaseSchemaFormComponent');
	@Input() readonly disabled = false;
	@Output() readonly modelChange = new EventEmitter<SchemaModel>();
	@Output() readonly errorChange = new EventEmitter<{ hasErrors: boolean }>();
	@Output() readonly schemaChange = new EventEmitter<ISchema>();

	schema?: ISchema;

	/** Model containing all fields (both visible and hidden) */
	model: SchemaModel = {};

	/** Previous state of the form model for tracking changes */
	previousFormState: SchemaModel = {};

	/** Parses the schema and handles locales. */
	parseSchemaByLocale(schema: ISchema): ISchema {
		if (!schema?.properties) return schema;
		const parsedSchema = structuredClone(schema);

		for (const key of Object.keys(schema.properties)) {
			parsedSchema.properties![key] = {
				...parsedSchema.properties![key],
				...this.i18n.getLocaleValue(parsedSchema.properties![key]?.locale),
			};
		}

		// Convert fields with isHiddenFromUser to use proper hidden widget
		this.convertHiddenFromUserFields(parsedSchema);

		return parsedSchema;
	}

	/**
	 * Converts fields with isHiddenFromUser property to use the 'hidden' form widget.
	 */
	private convertHiddenFromUserFields(schema: ISchema): void {
		if (!schema?.properties) return;

		for (const [key, prop] of Object.entries(schema.properties)) {
			if (prop?.isHiddenFromUser) {
				schema.properties[key] = { ...prop, widget: { id: 'hidden' } };
			}
		}
	}

	/** Sets required properties for schema. */
	setIsRequiredForSchemaProperties(schema?: ISchema): void {
		if (!schema?.properties) return;

		for (const [key, prop] of Object.entries(schema.properties)) {
			const isRequired = schema.required?.includes?.(key) ?? false;
			prop.isRequired = isRequired;
		}
	}

	/** Update the DOM to add "g-id" custom attribute to the parent of each label. */
	setDomIdentifiers(): void {
		if (!this.schema?.properties) return;
		const propertyMap = new Map(
			Object.entries(this.schema.properties).map(([key, property]) => {
				return [property.title ?? '', key];
			}),
		);
		setTimeout(() => this.iterateElements(propertyMap));
	}

	/** Recursively iterate through DOM elements to find matching labels and set "g-id" attribute. */
	private iterateElements(
		propertyMap: Map<string, string>,
		el: HTMLElement | undefined = undefined,
	): void {
		if (!el) el = this.element.nativeElement;
		const isLabel = el.tagName.toLowerCase() === 'label';
		const labelText = el.textContent ?? '';
		if (isLabel && propertyMap.has(labelText)) {
			el.parentElement?.setAttribute('g-id', propertyMap.get(labelText)!);
		} else {
			for (const child of el.children) {
				if ((child as HTMLElement)?.tagName) this.iterateElements(propertyMap, child as HTMLElement);
			}
		}
	}

	/**
	 * Initialize the component with a complete data model.
	 */
	initializeModel(initialData: SchemaModel): void {
		this.model = { ...initialData };
		this.previousFormState = { ...this.model };
	}

	/**
	 * Update the model and emit change events.
	 */
	setModelAndEmitChange(formValues: SchemaModel): void {
		this.debug('setModelAndEmitChange', this.previousFormState, this.model, formValues);

		// Update the model with new form values.
		for (const [key, value] of Object.entries(formValues)) {
			this.model[key] = value;
		}

		// For any fields that were in the previous state but not in the new form values,
		// set them to empty string to clear them
		for (const key of Object.keys(this.previousFormState)) {
			if (!(key in formValues)) {
				this.model[key] = '';
			}
		}

		this.previousFormState = { ...this.model };
		this.modelChange.emit(this.model);
	}

	/**
	 * Emits error state changes.
	 */
	emitErrorChange(errors: Record<string, unknown>[]): void {
		const hasRequiredErrors = validateRequired(this.model, this.schema);
		this.debug('emitErrorChange', { errors, hasRequiredErrors });
		this.errorChange.emit({ hasErrors: !isEmpty(errors) || hasRequiredErrors });
	}
}
